import {
  IamApi,
  iamErrorMessage,
  organizationDeactivateRequired,
  type OrganizationAccessUser,
  type OrganizationMember,
  type OrganizationMemberCandidate,
  type OrganizationRef,
} from 'api/IamApi'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import FavoriteStar from 'components/FavoriteStar/FavoriteStar'
import FavoritesOnlyChip from 'components/FavoritesOnlyChip/FavoritesOnlyChip'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import Title from 'components/Title/Title'
import UserAvatar from 'components/UserAvatar/UserAvatar'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { hasUnrestrictedDataAccess, useUser } from 'hooks/user'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'
import { PATH, PROGRESS_TYPES } from 'utils/constants'
import { sortWithFavoritesFirst } from 'utils/favoritesSort'
import getTestId from 'utils/getTestId'
import { flagParam, stringParam } from 'utils/urlQueryState'

import editorStyle from './IamUserEditor.module.css'
import usersStyle from './AccountUsers.module.css'
import style from './AccountOrganizations.module.css'

const personName = (person: { first_name: string; last_name: string; email: string }) => {
  const name = `${person.first_name} ${person.last_name}`.trim()
  return name || person.email
}

const AccountOrganizations = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useUser()
  const [url, setUrl] = useUrlQueryState({
    q: stringParam(),
    org: stringParam(),
    fav: flagParam(),
  })
  const [progress, setProgress] = useState(PROGRESS_TYPES.IDLE)
  const [rows, setRows] = useState<OrganizationRef[]>([])
  const [name, setName] = useState('')
  const [members, setMembers] = useState<OrganizationMember[]>([])
  const [memberCandidates, setMemberCandidates] = useState<OrganizationMemberCandidate[]>([])
  const [memberUserId, setMemberUserId] = useState<number | null>(null)
  const [grants, setGrants] = useState<OrganizationAccessUser[]>([])
  const [candidates, setCandidates] = useState<OrganizationAccessUser[]>([])
  const [grantUserId, setGrantUserId] = useState<number | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<OrganizationRef | null>(null)
  const [deactivateUserCount, setDeactivateUserCount] = useState<number | null>(null)
  const [pendingRemoveMember, setPendingRemoveMember] = useState<OrganizationMember | null>(null)
  const [pendingRevoke, setPendingRevoke] = useState<OrganizationAccessUser | null>(null)

  const canManage = Boolean(
    user && (hasUnrestrictedDataAccess(user.state) || user.state.audience === 'reseller')
  )
  const canGrantAccess = Boolean(user && hasUnrestrictedDataAccess(user.state))
  const editingId = url.org === 'new' ? 'new' : /^\d+$/.test(url.org) ? Number(url.org) : null

  const load = useCallback(async () => {
    setProgress(PROGRESS_TYPES.WORK)
    try {
      setRows(await IamApi.listOrganizations())
      setProgress(PROGRESS_TYPES.SUCCESS)
    } catch (error) {
      console.error('Error fetching organizations:', error)
      setProgress(PROGRESS_TYPES.ERROR)
    }
  }, [])

  useEffect(() => {
    if (canManage) load()
  }, [canManage, load])

  useEffect(() => {
    if (editingId == null) return
    if (editingId === 'new') {
      setName('')
      setMembers([])
      setMemberCandidates([])
      setGrants([])
      setCandidates([])
      setFormError(null)
      return
    }
    const current = rows.find(item => item.id === editingId)
    if (!current) return
    setName(current.name)
    setFormError(null)
    let cancelled = false
    Promise.all([
      IamApi.listOrganizationMembers(editingId),
      IamApi.listOrganizationAccess(editingId),
    ]).then(([people, access]) => {
      if (cancelled) return
      setMembers(people.members)
      setMemberCandidates(people.candidates)
      setMemberUserId(people.candidates[0]?.id ?? null)
      setGrants(access.grants)
      setCandidates(access.candidates)
      setGrantUserId(access.candidates[0]?.user_id ?? null)
    }).catch(error => {
      console.error('Error fetching organization detail:', error)
    })
    return () => {
      cancelled = true
    }
  }, [editingId, rows])

  const favoriteOrganizationIds = useMemo(
    () => new Set(user?.state.favoriteOrganizationIds ?? []),
    [user?.state.favoriteOrganizationIds]
  )

  const visibleRows = useMemo(() => {
    const query = url.q.trim().toLowerCase()
    const matched = rows.filter(item => {
      if (url.fav && !favoriteOrganizationIds.has(item.id)) return false
      if (!query) return true
      return item.name.toLowerCase().includes(query)
    })
    return sortWithFavoritesFirst(matched, favoriteOrganizationIds)
  }, [rows, url.q, url.fav, favoriteOrganizationIds])

  if (!canManage) {
    return <Navigate to={PATH.ACCOUNT_PROFILE} replace />
  }

  const closeEditor = () => setUrl({ org: '' })

  const onSave = async () => {
    const trimmedName = name.trim()
    if (!trimmedName || editingId == null) return
    setSaving(true)
    setFormError(null)
    const payload = { name: trimmedName }
    try {
      if (editingId === 'new') {
        await IamApi.createOrganization(payload)
      } else {
        await IamApi.updateOrganization(editingId, payload)
      }
      closeEditor()
      await load()
    } catch (error) {
      setFormError(
        iamErrorMessage(error, 'name')
        || iamErrorMessage(error)
        || t('account.organizations_save_error')
      )
    } finally {
      setSaving(false)
    }
  }

  const closeDelete = () => {
    setPendingDelete(null)
    setDeactivateUserCount(null)
  }

  const onDelete = async () => {
    if (!pendingDelete) return
    setFormError(null)
    try {
      await IamApi.deleteOrganization(pendingDelete.id, deactivateUserCount != null)
      closeDelete()
      await load()
    } catch (error) {
      const userCount = organizationDeactivateRequired(error)
      if (userCount != null && deactivateUserCount == null) {
        setDeactivateUserCount(userCount)
        return
      }
      closeDelete()
      setFormError(
        iamErrorMessage(error, 'detail')
        || iamErrorMessage(error)
        || t('account.organizations_delete_error')
      )
    }
  }

  const candidateOptions: IOption[] = candidates.map(person => ({
    value: String(person.user_id),
    text: `${personName(person)} (${person.email})`,
  }))
  const currentCandidate = candidateOptions.find(option => option.value === String(grantUserId)) || candidateOptions[0]

  const refreshAccess = async (orgId: number) => {
    const access = await IamApi.listOrganizationAccess(orgId)
    setGrants(access.grants)
    setCandidates(access.candidates)
    setGrantUserId(access.candidates[0]?.user_id ?? null)
  }

  const onGrant = async () => {
    if (editingId == null || editingId === 'new' || grantUserId == null) return
    setFormError(null)
    try {
      await IamApi.grantOrganizationAccess(editingId, grantUserId)
      await refreshAccess(editingId)
    } catch (error) {
      setFormError(iamErrorMessage(error, 'user_id') || iamErrorMessage(error) || t('account.organizations_grant_error'))
    }
  }

  const memberOptions: IOption[] = memberCandidates.map(person => ({
    value: String(person.id),
    text: `${personName(person)} (${person.email})`,
  }))
  const currentMember = memberOptions.find(option => option.value === String(memberUserId)) || memberOptions[0]

  const refreshMembers = async (orgId: number) => {
    const people = await IamApi.listOrganizationMembers(orgId)
    setMembers(people.members)
    setMemberCandidates(people.candidates)
    setMemberUserId(people.candidates[0]?.id ?? null)
  }

  const onAddMember = async () => {
    if (editingId == null || editingId === 'new' || memberUserId == null) return
    setFormError(null)
    try {
      await IamApi.addOrganizationMember(editingId, memberUserId)
      await refreshMembers(editingId)
      await load()
    } catch (error) {
      setFormError(iamErrorMessage(error, 'user_id') || iamErrorMessage(error) || t('account.organizations_members_error'))
    }
  }

  const onRemoveMember = async () => {
    if (editingId == null || editingId === 'new' || !pendingRemoveMember) return
    setFormError(null)
    try {
      await IamApi.removeOrganizationMember(editingId, pendingRemoveMember.id)
      setPendingRemoveMember(null)
      await refreshMembers(editingId)
      await load()
    } catch (error) {
      setPendingRemoveMember(null)
      setFormError(iamErrorMessage(error) || t('account.organizations_members_error'))
    }
  }

  const onRevoke = async () => {
    if (editingId == null || editingId === 'new' || !pendingRevoke) return
    setFormError(null)
    try {
      await IamApi.revokeOrganizationAccess(editingId, pendingRevoke.user_id)
      setPendingRevoke(null)
      await refreshAccess(editingId)
    } catch (error) {
      setPendingRevoke(null)
      setFormError(iamErrorMessage(error) || t('account.organizations_grant_error'))
    }
  }

  if (editingId != null) {
    return (
      <div className={style.panel} data-test-id={getTestId('account-organizations-editor')}>
        <div className={style.headerArea}>
          <button type="button" className={style.backLink} onClick={closeEditor}>
            {t('common.back')}
          </button>
          <Title text={editingId === 'new' ? t('account.organizations_create_title') : t('account.organizations_edit_title')} />
        </div>
        <form
          className={style.form}
          onSubmit={event => {
            event.preventDefault()
            onSave()
          }}
        >
          <TextFieldDescribed
            description={t('account.organizations_name')}
            placeholder={t('account.organizations_name')}
            value={name}
            onChange={event => setName(event.target.value)}
            labelClassName={editorStyle.formLabel}
            dataTestId={getTestId('org-name')}
          />
          {formError ? <div className={style.errorText}>{formError}</div> : null}
          <div className={style.formActions}>
            <Button.Standart
              type="submit"
              disabled={saving || !name.trim()}
              dataTestId="org-save"
            >
              {t('common.save')}
            </Button.Standart>
            <Button.Outlined type="button" onClick={closeEditor}>
              {t('common.cancel')}
            </Button.Outlined>
          </div>
        </form>
        {editingId !== 'new' ? (
          <>
            <section className={style.section}>
              <div className={style.sectionTitle}>{t('account.organizations_members')}</div>
              {memberOptions.length > 0 ? (
                <div className={style.grantRow}>
                  <Dropdown
                    options={memberOptions}
                    currentOption={currentMember}
                    placeholder={t('account.organizations_members_pick')}
                    description={t('account.organizations_members_pick')}
                    descriptionClassName={editorStyle.formLabel}
                    onClickOption={option => setMemberUserId(Number(option.value))}
                    dataTestId="org-member-user"
                  />
                  <Button.Outlined onClick={onAddMember} dataTestId="org-member-add">
                    {t('common.add')}
                  </Button.Outlined>
                </div>
              ) : null}
              <div className={style.peopleTable}>
                <div className={clsx(usersStyle.tableHeader, style.peopleHeader)}>
                  <div>{t('account.users_col_name')}</div>
                  <div>{t('account.email_label')}</div>
                  <div>{t('account.users_role')}</div>
                  <div>{t('account.users_status')}</div>
                  <div />
                </div>
                {members.length === 0 ? (
                  <div className={usersStyle.empty}>{t('account.organizations_members_empty')}</div>
                ) : (
                  members.map(member => (
                    <div
                      key={member.id}
                      className={clsx(
                        usersStyle.tableRow,
                        style.peopleRow,
                        member.can_open && style.peopleRowOpen,
                        !member.is_active && usersStyle.tableRowInactive,
                      )}
                      onClick={() => {
                        if (member.can_open) navigate(`${PATH.ACCOUNT_USERS}?user=${member.id}`)
                      }}
                      role={member.can_open ? 'link' : undefined}
                    >
                      <div className={usersStyle.nameCell}>
                        <UserAvatar
                          url={member.avatar_url}
                          firstName={member.first_name}
                          lastName={member.last_name}
                          email={member.email}
                          size={28}
                        />
                        <div className={usersStyle.cell}>{personName(member)}</div>
                      </div>
                      <div className={usersStyle.cell}>{member.email}</div>
                      <div className={usersStyle.cell}>{t(`account.audience_${member.audience}`)}</div>
                      <div>
                        <span className={clsx(usersStyle.statusBadge, member.is_active ? usersStyle.statusActive : usersStyle.statusInactive)}>
                          {member.is_active ? t('account.users_filter_active') : t('account.users_filter_inactive')}
                        </span>
                      </div>
                      <div className={style.actions} onClick={event => event.stopPropagation()}>
                        {member.can_open ? (
                          <Button.Outlined
                            onClick={() => setPendingRemoveMember(member)}
                            dataTestId={`org-member-remove-${member.id}`}
                          >
                            {t('account.organizations_members_remove')}
                          </Button.Outlined>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
            <section className={style.section}>
              <div className={style.sectionTitle}>{t('account.organizations_access_title')}</div>
              <div className={style.hint}>{t('account.organizations_access_hint')}</div>
              {canGrantAccess && candidateOptions.length > 0 ? (
                <div className={style.grantRow}>
                  <Dropdown
                    options={candidateOptions}
                    currentOption={currentCandidate}
                    placeholder={t('account.organizations_access_pick')}
                    description={t('account.organizations_access_pick')}
                    descriptionClassName={editorStyle.formLabel}
                    onClickOption={option => setGrantUserId(Number(option.value))}
                    dataTestId="org-access-reseller"
                  />
                  <Button.Outlined onClick={onGrant} dataTestId="org-access-add">
                    {t('common.add')}
                  </Button.Outlined>
                </div>
              ) : null}
              <div className={style.peopleTable}>
                <div className={clsx(usersStyle.tableHeader, style.accessHeader)}>
                  <div>{t('account.users_col_name')}</div>
                  <div>{t('account.email_label')}</div>
                  <div />
                </div>
                {grants.length === 0 ? (
                  <div className={usersStyle.empty}>{t('account.organizations_access_empty')}</div>
                ) : (
                  grants.map(person => (
                    <div key={person.user_id} className={clsx(usersStyle.tableRow, style.accessRow)}>
                      <div className={usersStyle.nameCell}>
                        <UserAvatar
                          firstName={person.first_name}
                          lastName={person.last_name}
                          email={person.email}
                          size={28}
                        />
                        <div className={usersStyle.cell}>{personName(person)}</div>
                      </div>
                      <div className={usersStyle.cell}>{person.email}</div>
                      <div className={style.actions}>
                        {canGrantAccess ? (
                          <Button.Outlined
                            onClick={() => setPendingRevoke(person)}
                            dataTestId={`org-access-remove-${person.user_id}`}
                          >
                            {t('common.delete')}
                          </Button.Outlined>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </>
        ) : null}
        <ConfirmDialog
          isOpen={pendingRemoveMember != null}
          onClose={() => setPendingRemoveMember(null)}
          onConfirmClick={onRemoveMember}
          title={t('account.organizations_members_remove_title')}
          confirmText={t('account.organizations_members_remove')}
          danger
        >
          {t('account.organizations_members_remove_body', {
            name: pendingRemoveMember ? personName(pendingRemoveMember) : '',
          })}
        </ConfirmDialog>
        <ConfirmDialog
          isOpen={pendingRevoke != null}
          onClose={() => setPendingRevoke(null)}
          onConfirmClick={onRevoke}
          title={t('account.organizations_access_remove_title')}
          confirmText={t('common.delete')}
          danger
        >
          {t('account.organizations_access_remove_body', {
            name: pendingRevoke ? personName(pendingRevoke) : '',
          })}
        </ConfirmDialog>
      </div>
    )
  }

  return (
    <div className={style.panel} data-test-id={getTestId('account-organizations')}>
      <div className={style.headerArea}>
        <div className={style.titleBlock}>
          <div>
            <Title text={t('account.organizations_title')} />
            <div className={style.subtitle}>{t('account.organizations_subtitle')}</div>
          </div>
          <Button.Standart onClick={() => setUrl({ org: 'new' })} dataTestId="org-create">
            {t('account.organizations_create')}
          </Button.Standart>
        </div>
        <div className={style.filtersRow}>
          <input
            className={style.searchInput}
            value={url.q}
            onChange={event => setUrl({ q: event.target.value })}
            placeholder={t('common.search')}
            data-test-id={getTestId('org-search')}
          />
          <FavoritesOnlyChip pressed={url.fav} onClick={() => setUrl({ fav: !url.fav })} />
        </div>
        {formError ? <div className={style.errorText}>{formError}</div> : null}
      </div>
      <div className={style.tableContainer}>
        <div className={style.tableHeader}>
          <div />
          <div>{t('account.organizations_name')}</div>
          <div className={style.hideMobile}>{t('account.organizations_people')}</div>
          <div className={style.hideMobile}>{t('account.organizations_status')}</div>
          <div />
        </div>
        <div className={style.table}>
          {progress === PROGRESS_TYPES.ERROR ? (
            <div className={style.empty}>{t('account.organizations_load_error')}</div>
          ) : visibleRows.length === 0 && progress === PROGRESS_TYPES.SUCCESS ? (
            url.fav && favoriteOrganizationIds.size === 0 ? (
              <div className={style.empty}>
                <div>
                  <div className={style.emptyTitle}>{t('favorites.empty_organizations')}</div>
                  <div>{t('favorites.empty_organizations_description')}</div>
                </div>
              </div>
            ) : (
              <div className={style.empty}>{t('account.organizations_empty')}</div>
            )
          ) : (
            visibleRows.map(org => (
              <div className={style.tableRow} key={org.id}>
                <FavoriteStar kind="organization" id={org.id} />
                <div className={style.cell}>{org.name}</div>
                <div className={`${style.cell} ${style.hideMobile}`}>{org.member_count}</div>
                <div className={`${style.cell} ${style.hideMobile}`}>
                  {org.is_active ? t('account.organizations_active') : t('account.organizations_inactive')}
                </div>
                <div className={style.actions}>
                  {org.is_active ? (
                    <Button.Outlined
                      onClick={() => setUrl({ org: String(org.id) })}
                      dataTestId={`org-edit-${org.id}`}
                    >
                      {t('common.edit')}
                    </Button.Outlined>
                  ) : null}
                  {org.is_active ? (
                    <Button.Outlined
                      onClick={() => {
                        setDeactivateUserCount(null)
                        setPendingDelete(org)
                      }}
                      dataTestId={`org-delete-${org.id}`}
                    >
                      {t('account.organizations_delete')}
                    </Button.Outlined>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={pendingDelete != null}
        onClose={closeDelete}
        onConfirmClick={onDelete}
        title={t('account.organizations_delete_title')}
        confirmText={t('account.organizations_delete')}
        danger
      >
        {deactivateUserCount == null
          ? t('account.organizations_delete_body')
          : t('account.organizations_delete_users_body', { count: deactivateUserCount })}
      </ConfirmDialog>
    </div>
  )
}

export default AccountOrganizations
